import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Grid2x2, AlertCircle } from "lucide-react";
import { adminListProducts, adminGetPricingMatrix } from "../../lib/admin";

const fmtUSD = (n) =>
  Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

const PricingMatrixTab = () => {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingMatrix, setLoadingMatrix] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const list = await adminListProducts();
        const active = list.filter((p) => p.is_active);
        setProducts(active);
        if (active.length > 0) setSelected(active[0].id);
      } catch {
        toast.error("Failed to load products");
      } finally {
        setLoadingProducts(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoadingMatrix(true);
    adminGetPricingMatrix(selected)
      .then(setMatrix)
      .catch(() => toast.error("Failed to compute matrix"))
      .finally(() => setLoadingMatrix(false));
  }, [selected]);

  const qtys = matrix?.quantities || [];
  const selectedProduct = useMemo(
    () => products.find((p) => p.id === selected),
    [products, selected]
  );

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <p className="text-sm text-[color:var(--ups-subtext)] max-w-xl">
          Live retail & UPS Store prices computed from the catalog for every size × paper combination at canonical quantities. Updates instantly when you change product or library pricing.
        </p>
        <div className="flex items-center gap-2">
          <span className="overline">Product</span>
          <select
            className="input-swiss px-3 py-2 text-sm rounded-sm min-w-[220px]"
            value={selected || ""}
            onChange={(e) => setSelected(e.target.value)}
            disabled={loadingProducts}
            data-testid="pricing-matrix-product-select"
          >
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} {p.price_strategy !== "formula" ? `· ${p.price_strategy}` : ""}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedProduct && (
        <div className="card-swiss rounded-sm p-4 mb-4" style={{ borderLeft: "4px solid var(--ups-gold)" }}>
          <div className="flex items-start gap-3">
            <div
              className="w-9 h-9 flex items-center justify-center shrink-0 mt-0.5"
              style={{ background: "#FFB500", color: "var(--ups-ink)" }}
            >
              <Grid2x2 size={16} strokeWidth={2.5} />
            </div>
            <div className="flex-1">
              <div className="font-display text-lg font-bold">{selectedProduct.name}</div>
              <div className="text-[11px] font-mono text-[color:var(--ups-subtext)] uppercase tracking-[0.12em] mt-0.5">
                {selectedProduct.category} · strategy: {selectedProduct.price_strategy || "formula"}
                {selectedProduct.has_pages ? ` · pages=${matrix?.product?.default_pages || "?"}` : ""}
                {matrix?.product?.default_finish_ids?.length ? ` · finishes: ${matrix.product.default_finish_ids.join(", ")}` : ""}
              </div>
              {selectedProduct.pricing_notes && (
                <p className="text-xs text-[color:var(--ups-subtext)] mt-2 whitespace-pre-wrap">
                  {selectedProduct.pricing_notes}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {loadingMatrix ? (
        <div className="flex items-center gap-2 text-sm text-[color:var(--ups-subtext)]">
          <Loader2 size={14} className="animate-spin" /> Computing matrix…
        </div>
      ) : matrix && matrix.rows.length > 0 ? (
        <div className="card-swiss rounded-sm overflow-x-auto" data-testid="pricing-matrix-table">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr style={{ background: "var(--ups-muted)" }}>
                <th className="text-left px-3 py-3 text-[11px] font-bold uppercase tracking-[0.12em] border-b" style={{ borderColor: "var(--ups-border)" }}>Size</th>
                <th className="text-left px-3 py-3 text-[11px] font-bold uppercase tracking-[0.12em] border-b" style={{ borderColor: "var(--ups-border)" }}>Paper</th>
                {qtys.map((q) => (
                  <th key={q} className="text-right px-3 py-3 text-[11px] font-bold uppercase tracking-[0.12em] border-b font-mono" style={{ borderColor: "var(--ups-border)" }}>
                    {q.toLocaleString()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.rows.map((row, idx) => (
                <tr
                  key={`${row.size_id}-${row.paper_id}`}
                  className={idx % 2 === 0 ? "" : "bg-[color:var(--ups-muted)]/40"}
                  data-testid={`pricing-matrix-row-${row.size_id}-${row.paper_id}`}
                >
                  <td className="px-3 py-2.5 border-b font-mono text-[12px]" style={{ borderColor: "var(--ups-border)" }}>
                    {row.size_id}
                  </td>
                  <td className="px-3 py-2.5 border-b font-mono text-[11px] text-[color:var(--ups-subtext)] truncate max-w-[200px]" style={{ borderColor: "var(--ups-border)" }}>
                    {row.paper_id}
                  </td>
                  {row.cells.map((c, i) => (
                    <td key={i} className="px-3 py-2.5 text-right border-b" style={{ borderColor: "var(--ups-border)" }}>
                      {c.error ? (
                        <span title={c.error} className="inline-flex items-center gap-1 text-[10px] text-[color:var(--ups-subtext)]">
                          <AlertCircle size={12} /> n/a
                        </span>
                      ) : (
                        <div className="font-mono leading-tight">
                          <div className="text-[11px] text-[color:var(--ups-subtext)] line-through">
                            {fmtUSD(c.retail)}
                          </div>
                          <div className="font-bold" style={{ color: "var(--ups-accent)" }}>
                            {fmtUSD(c.ups)}
                          </div>
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="p-3 text-[11px] font-mono text-[color:var(--ups-subtext)] border-t" style={{ borderColor: "var(--ups-border)" }}>
            Each cell shows retail (strikethrough) and UPS Store price (40% off, bold green). Columns are quantities.
          </div>
        </div>
      ) : (
        <div className="p-6 text-sm text-[color:var(--ups-subtext)]">No pricing data.</div>
      )}
    </div>
  );
};

export default PricingMatrixTab;
